import db from '../../../../services/firebase'
import { doc, getDoc } from 'firebase/firestore'

// Key for storing last activity timestamp in localStorage
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp'

// Throttle time in milliseconds (30 seconds)
const THROTTLE_TIME = 30 * 1000

// Last update timestamp to throttle updates
let lastUpdateTime = 0

/**
 * Updates the last activity timestamp in localStorage, but throttled to reduce writes
 */
export const updateLastActivity = (): void => {
  const now = Date.now()
  
  // Only update if it's been more than THROTTLE_TIME since the last update
  if (now - lastUpdateTime > THROTTLE_TIME) {
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
    lastUpdateTime = now
  }
}

/**
 * Gets the last activity timestamp from localStorage
 * @returns The last activity timestamp or null if not found
 */
export const getLastActivity = (): number | null => {
  const timestamp = localStorage.getItem(LAST_ACTIVITY_KEY)
  return timestamp ? parseInt(timestamp, 10) : null
}

/**
 * Checks if the session has timed out based on tenant settings
 * @param tenantId The tenant ID
 * @returns Promise<boolean> True if the session has timed out, false otherwise
 */
export const checkSessionTimeout = async (tenantId: string): Promise<boolean> => {
  if (!tenantId) return false

  try {
    // Get the last activity timestamp
    const lastActivity = getLastActivity()
    if (!lastActivity) return false

    // Check if we have a cached timeout value in localStorage
    const cachedTimeoutKey = `sessionTimeout_${tenantId}`
    const cachedTimeout = localStorage.getItem(cachedTimeoutKey)
    const cachedTimestamp = localStorage.getItem(`${cachedTimeoutKey}_timestamp`)
    
    // Default timeout (30 minutes)
    let timeoutDuration = 30 * 60 * 1000

    // If we have a cached value and it's less than 1 hour old, use it
    if (cachedTimeout && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10)
      const now = Date.now()
      const oneHour = 60 * 60 * 1000
      
      if (now - timestamp < oneHour) {
        timeoutDuration = parseInt(cachedTimeout, 10)
      } else {
        // If the cached value is too old, fetch from Firestore
        const tenantRef = doc(db, 'tenants', tenantId)
        const securityRef = doc(tenantRef, 'settings', 'security')
        const securityDoc = await getDoc(securityRef)

        if (securityDoc.exists() && securityDoc.data().sessionTimeoutMs) {
          timeoutDuration = securityDoc.data().sessionTimeoutMs
          
          // Cache the timeout value in localStorage
          localStorage.setItem(cachedTimeoutKey, timeoutDuration.toString())
          localStorage.setItem(`${cachedTimeoutKey}_timestamp`, Date.now().toString())
        }
      }
    } else {
      // If we don't have a cached value, fetch from Firestore
      const tenantRef = doc(db, 'tenants', tenantId)
      const securityRef = doc(tenantRef, 'settings', 'security')
      const securityDoc = await getDoc(securityRef)

      if (securityDoc.exists() && securityDoc.data().sessionTimeoutMs) {
        timeoutDuration = securityDoc.data().sessionTimeoutMs
        
        // Cache the timeout value in localStorage
        localStorage.setItem(cachedTimeoutKey, timeoutDuration.toString())
        localStorage.setItem(`${cachedTimeoutKey}_timestamp`, Date.now().toString())
      }
    }

    // Calculate if the session has timed out
    const currentTime = Date.now()
    const inactiveTime = currentTime - lastActivity

    return inactiveTime >= timeoutDuration
  } catch (error) {
    console.error('Error checking session timeout:', error)
    return false
  }
}

/**
 * Initialize session activity tracking
 * @returns A cleanup function to remove event listeners
 */
export const initSessionTracking = (): () => void => {
  // Set initial activity timestamp
  updateLastActivity()

  const activityEvents = [
    'mousedown', // Captures clicks and mouse interactions
    'keydown',   // Captures keyboard interactions
    'touchstart' // Captures touch interactions on mobile
  ]

  // Add event listeners to update the last activity timestamp
  activityEvents.forEach(event => {
    window.addEventListener(event, updateLastActivity)
  })

  // Return a cleanup function
  return () => {
    activityEvents.forEach(event => {
      window.removeEventListener(event, updateLastActivity)
    })
  }
}